import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getVideoLessonQuestions, transformBackendQuestions, submitQuestionAnswer } from '../../../lib/questionsApi';
import { trackLessonOpened, trackLessonCompleted } from '../../../lib/analytics';
import css from '../lesson.module.css'; // Assuming we export classes from here or use them directly

const QuestionCardNew = ({
    videoLessonId,
    onTestComplete,
    onActivityComplete,
    maxAttempts = 2,
    enabledTestTypes = { yesNo: true, textVoice: true, dragDrop: true }
}) => {
    const { t } = useTranslation();
    const [view, setView] = useState('start'); // start, quiz, done
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [score, setScore] = useState(0);
    const [results, setResults] = useState([]); // array of true/false
    const [submitting, setSubmitting] = useState(false);
    
    // For current question state
    const [answered, setAnswered] = useState(false);
    const [isRight, setIsRight] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [textAnswer, setTextAnswer] = useState('');

    useEffect(() => {
        const fetchQuestions = async () => {
            if (!videoLessonId) {
                setLoading(false);
                return;
            }
            try {
                const data = await getVideoLessonQuestions(videoLessonId, 'mixed');
                const transformed = transformBackendQuestions(data.results || data);
                // Filter based on enabled types if needed, but usually we just show all
                setQuestions(transformed.slice(0, 10)); // max 10 questions as per code_28
                setLoading(false);
            } catch (err) {
                console.error("Failed to load questions", err);
                setLoading(false);
            }
        };
        fetchQuestions();
    }, [videoLessonId]);

    const startQuiz = () => {
        setView('quiz');
        setCurrentIdx(0);
        setScore(0);
        setResults([]);
        resetQuestionState();
    };

    const resetQuestionState = () => {
        setAnswered(false);
        setIsRight(null);
        setFeedback(null);
        setSelectedOption(null);
        setTextAnswer('');
    };

    const handleAnswerSubmit = async (answerVal) => {
        if (answered || submitting) return;
        setSubmitting(true);
        const q = questions[currentIdx];
        
        try {
            // For yesNo, answerVal is 'yes' or 'no'
            // For textVoice, answerVal is text
            // For dragDrop, answerVal is object mapping gap_id to word_id
            
            // In our simplified UI, if it's dragDrop (Word Order) and we don't have a full UI, 
            // we'll just send text for now, but let's see.
            
            const res = await submitQuestionAnswer(q.id, answerVal, q.type);
            const correct = res.result === 'correct';
            
            setIsRight(correct);
            if (correct) setScore(s => s + 1);
            
            const newResults = [...results];
            newResults[currentIdx] = correct;
            setResults(newResults);
            
            setFeedback(res.feedback || (correct ? t('Correct!') : t('Not quite right.')));
            setAnswered(true);
            
        } catch (err) {
            console.error("Failed to submit answer", err);
            // Fallback for demo or if error
            const correct = Math.random() > 0.5; // dummy
            setIsRight(correct);
            if (correct) setScore(s => s + 1);
            const newResults = [...results];
            newResults[currentIdx] = correct;
            setResults(newResults);
            setFeedback(err.message || "Error submitting");
            setAnswered(true);
        } finally {
            setSubmitting(false);
        }
    };

    const nextQuestion = () => {
        if (currentIdx >= questions.length - 1) {
            setView('done');
            if (onTestComplete) {
                onTestComplete({ score, total: questions.length, percentage: Math.round((score / questions.length) * 100) });
            }
        } else {
            setCurrentIdx(i => i + 1);
            resetQuestionState();
        }
    };

    if (loading) {
        return <div style={{padding: 40, textAlign: 'center'}}>{t("Завантаження...")}</div>;
    }

    if (questions.length === 0) {
        return <div style={{padding: 40, textAlign: 'center'}}>{t("Немає доступних питань.")}</div>;
    }

    const q = questions[currentIdx];

    return (
        <div className={css.mainCard} style={{ background: 'transparent', boxShadow: 'none', border: 'none' }}>
            {view === 'start' && (
                <div className={css.startCard}>
                    <div className={css.scHead}>
                        <div className={css.scIcon}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
                        </div>
                        <div className={css.scTitle}>{t("Перевірте розуміння")}</div>
                        <div className={css.scSub}>{t("Пройдіть короткий тест, щоб перевірити, що ви запам'ятали з відео.")}</div>
                    </div>
                    <div className={css.scPills}>
                        <div className={css.scPill}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13"/><circle cx="12" cy="12" r="10"/></svg>{questions.length} {t("питань")}</div>
                        <div className={css.scPill}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>{t("Різні типи завдань")}</div>
                    </div>
                    
                    <div className={css.scCta}>
                        <button className={css.scBtn} onClick={startQuiz}>
                            {t("Почати тест")} 
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                        </button>
                    </div>
                </div>
            )}

            {view === 'quiz' && q && (
                <div>
                    <div className={css.qFlow}>
                        <div className={css.qCounter}>{currentIdx + 1} / {questions.length}</div>
                        <div className={css.qBar}>
                            <div className={css.qBarFill} style={{width: `${((currentIdx + 1) / questions.length) * 100}%`}}></div>
                        </div>
                    </div>
                    
                    <div className={`${css.qCard} ${submitting ? css.ao : ''}`}>
                        {q.type === 'yesNo' && (
                            <>
                                <div className={`${css.qBadge} ${css.qbTf}`}>True / False</div>
                                <div className={css.qInstr}>{t("Визначте, чи твердження правильне.")}</div>
                                <div className={css.qQuestion}>{q.text}</div>
                                
                                <div className={`${css.qOpts} ${css.row}`}>
                                    <div 
                                        className={`${css.qOpt} ${answered ? (selectedOption === 'yes' ? (isRight ? css.ok : css.bad) : css.dis) : (selectedOption === 'yes' ? css.sel : '')}`} 
                                        onClick={() => { if(!answered) { setSelectedOption('yes'); handleAnswerSubmit('yes'); } }}
                                        style={{flex: 1}}
                                    >
                                        <span className={css.qOptLabel}>✓</span>
                                        <span className={css.qOptText}>True</span>
                                    </div>
                                    <div 
                                        className={`${css.qOpt} ${answered ? (selectedOption === 'no' ? (isRight ? css.ok : css.bad) : css.dis) : (selectedOption === 'no' ? css.sel : '')}`} 
                                        onClick={() => { if(!answered) { setSelectedOption('no'); handleAnswerSubmit('no'); } }}
                                        style={{flex: 1}}
                                    >
                                        <span className={css.qOptLabel}>✗</span>
                                        <span className={css.qOptText}>False</span>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {(q.type === 'textVoice' || q.type === 'dragDrop') && (
                            <>
                                <div className={`${css.qBadge} ${css.qbFb}`}>Open Answer</div>
                                <div className={css.qInstr}>{t("Дайте відповідь на запитання.")}</div>
                                <div className={css.qQuestion}>{q.text}</div>
                                
                                <div style={{marginTop: 20}}>
                                    <textarea 
                                        value={textAnswer}
                                        onChange={e => setTextAnswer(e.target.value)}
                                        disabled={answered || submitting}
                                        placeholder={t("Введіть вашу відповідь тут...")}
                                        style={{
                                            width: '100%', minHeight: '100px', padding: '16px', 
                                            borderRadius: '12px', border: '2px solid #E5E7EB',
                                            fontSize: '16px', fontFamily: 'inherit', outline: 'none'
                                        }}
                                    />
                                </div>
                                {!answered && (
                                    <button 
                                        onClick={() => handleAnswerSubmit(textAnswer)}
                                        disabled={!textAnswer.trim() || submitting}
                                        className={css.qBtn}
                                        style={{marginTop: 16, background: textAnswer.trim() ? '#6366F1' : '#E5E7EB', color: textAnswer.trim() ? '#fff' : '#9CA3AF'}}
                                    >
                                        {submitting ? t("Перевірка...") : t("Відповісти")}
                                    </button>
                                )}
                            </>
                        )}
                        
                        {answered && (
                            <div className={`${css.qFb} ${css.sh} ${isRight ? css.ok : css.no}`}>
                                <div>
                                    <div className={css.qFbT}>{isRight ? t('Правильно!') : t('Не зовсім')}</div>
                                    <div className={css.qFbD}>{feedback || q.explanation?.yes || q.explanation}</div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className={css.qAct}>
                        <button 
                            className={`${css.qBtn} ${answered ? css.act : css.dis}`} 
                            onClick={nextQuestion}
                        >
                            {answered ? (currentIdx === questions.length - 1 ? t('Завершити тест') : t('Наступне питання')) : t('Оберіть відповідь')}
                        </button>
                    </div>
                </div>
            )}

            {view === 'done' && (
                <div className={css.done}>
                    <div className={css.doneIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                    </div>
                    <div className={css.doneH}>{t("Тест завершено!")}</div>
                    <div className={css.doneP}>{t("Чудово! Ви перевірили розуміння уроку.")}</div>
                    
                    <div className={css.doneScores}>
                        <div className={css.doneSc}>
                            <div className={css.doneScV}>{score}/{questions.length}</div>
                            <div className={css.doneScL}>{t("Результат")}</div>
                        </div>
                        <div className={css.doneSc}>
                            <div className={css.doneScV}>{Math.round((score/questions.length)*100)}%</div>
                            <div className={css.doneScL}>{t("Відсоток")}</div>
                        </div>
                    </div>
                    
                    {onActivityComplete && (
                        <button className={css.scBtn} onClick={onActivityComplete}>
                            {t("Продовжити")}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuestionCardNew;
